import DevSection from './DevSection'
import { isDevToolAvailable } from '../../../devToolCapabilities'
import type { DataMode } from '../../../dataMode'

export interface SeasonSectionProps {
  mode: DataMode | null
  hasGames: boolean
  resultsRevealed: boolean
  isSimulatingGameweek: boolean
  onSimulateDeadline: () => void
  onToggleResults: () => void
  onFinalizeGameweek: () => void
  onSimulateSeason: () => void
  onSimulateGameweekRealtime?: () => void
  onDevChangeGameweek: (delta: number) => void
}

/**
 * The season simulation controls, extracted from the rail unchanged.
 *
 * Every control here writes an *outcome* — the deadline flag, everyone's `season_history`, the
 * shared gameweek pointer — which is exactly right in `mock`, where the bundled season is already
 * played and there is no pipeline to drive, and wrong everywhere else: in `replica` the scheduler
 * re-derives those same rows on its next tick and the last writer wins ("the
 * principle"). So the whole section is absent outside `mock`, apart from "play live", which has a
 * replica implementation of its own that drives the clock instead.
 */
export default function SeasonSection({
  mode,
  hasGames,
  resultsRevealed,
  isSimulatingGameweek,
  onSimulateDeadline,
  onToggleResults,
  onFinalizeGameweek,
  onSimulateSeason,
  onSimulateGameweekRealtime,
  onDevChangeGameweek,
}: SeasonSectionProps) {
  const can = (tool: Parameters<typeof isDevToolAvailable>[0]) => isDevToolAvailable(tool, mode)
  const showStepper = can('changeGameweek')
  const showPlayLive = can('playLive') && hasGames && onSimulateGameweekRealtime !== undefined
  const showSimulation = can('simulateDeadline') && hasGames
  if (!showStepper && !showPlayLive && !showSimulation) return null

  return (
    <DevSection title="Season">
      {showStepper && (
        <button
          className="gameweek-grid__dev-btn"
          onClick={() => onDevChangeGameweek(-1)}
          title="Dev: previous gameweek"
        >
          <span className="gameweek-grid__dev-btn-label">Prev GW</span>◀
        </button>
      )}
      {showSimulation && (
        <>
          <button
            className="gameweek-grid__dev-btn"
            onClick={onSimulateDeadline}
            title="Dev: simulate deadline passed"
          >
            <span className="gameweek-grid__dev-btn-label">Deadline</span>🛠
          </button>
          <button
            className="gameweek-grid__dev-btn"
            onClick={onToggleResults}
            title="Dev: toggle results revealed"
          >
            <span className="gameweek-grid__dev-btn-label">Reveal</span>
            {resultsRevealed ? '🙈' : '🏁'}
          </button>
          <button
            className="gameweek-grid__dev-btn"
            onClick={onFinalizeGameweek}
            title="Dev: finalize gameweek and advance"
          >
            <span className="gameweek-grid__dev-btn-label">Finalize</span>✅
          </button>
          <button
            className="gameweek-grid__dev-btn gameweek-grid__dev-btn--bulk"
            onClick={onSimulateSeason}
            title="Dev: simulate rest of season"
          >
            <span className="gameweek-grid__dev-btn-label">Sim season</span>⏩
          </button>
        </>
      )}
      {showPlayLive && (
        <button
          className="gameweek-grid__dev-btn"
          onClick={onSimulateGameweekRealtime}
          // In replica the control stays live while a run is in flight, because pressing it again
          // is how you freeze the clock — a disabled button would strand it at 60×.
          disabled={isSimulatingGameweek && mode !== 'replica'}
          title={
            mode === 'replica'
              ? 'Dev: run the gameweek against the replica clock (press again to freeze)'
              : 'Dev: play gameweek in real time (30s)'
          }
        >
          <span className="gameweek-grid__dev-btn-label">
            {mode === 'replica'
              ? isSimulatingGameweek
                ? 'Freeze run'
                : 'Run gameweek'
              : 'Play live'}
          </span>
          🎬
        </button>
      )}
      {showStepper && (
        <button
          className="gameweek-grid__dev-btn"
          onClick={() => onDevChangeGameweek(1)}
          title="Dev: next gameweek"
        >
          <span className="gameweek-grid__dev-btn-label">Next GW</span>▶
        </button>
      )}
    </DevSection>
  )
}
